import { createContext, useCallback, useContext, useState } from "react";

export type FileDetailViewerContextZoom = number;
export type FileDetailViewerContextPageInfo = { current: number; total: number };
export type FileDetailViewerContextPageInfoDelegate = (info: FileDetailViewerContextPageInfo) => void;

interface FileDetailViewerContextProps {
  zoom: FileDetailViewerContextZoom;
  setZoom: (zoom: FileDetailViewerContextZoom) => void;
  setPageInfo: (info: FileDetailViewerContextPageInfo) => void;
  addPageInfoDelegate: (delegate: FileDetailViewerContextPageInfoDelegate) => void;
  removePageInfoDelegate: (delegate: FileDetailViewerContextPageInfoDelegate) => void;
}

const BASE = 600;
const WIDTH = 900;

const defaultZoom = Math.min(1.0, Math.max(0.4, Math.floor(((window.innerWidth - BASE) / WIDTH) * 10) / 10));

const FileDetailViewerContext = createContext<FileDetailViewerContextProps>({
  zoom: defaultZoom,
  setZoom: () => {},
  setPageInfo: () => ({ current: -1, total: 0 }),
  addPageInfoDelegate: () => {},
  removePageInfoDelegate: () => {},
});

function FileDetailViewerContextProvider({ children }: { children: React.ReactNode }) {
  const [zoom, setZoom] = useState<FileDetailViewerContextZoom>(defaultZoom);
  const [delegates] = useState<Map<FileDetailViewerContextPageInfoDelegate, true>>(
    new Map<FileDetailViewerContextPageInfoDelegate, true>()
  );
  const addPageInfoDelegate = useCallback((delegate: FileDetailViewerContextPageInfoDelegate) => {
    if (delegates.get(delegate)) return;
    delegates.set(delegate, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const removePageInfoDelegate = useCallback((delegate: FileDetailViewerContextPageInfoDelegate) => {
    if (!delegates.get(delegate)) return;
    delegates.delete(delegate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setPageInfo = useCallback((pageInfo: FileDetailViewerContextPageInfo) => {
    for (const fn of Array.from(delegates.keys())) {
      fn(pageInfo);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FileDetailViewerContext.Provider
      value={{
        zoom,
        setZoom,
        setPageInfo,
        addPageInfoDelegate,
        removePageInfoDelegate,
      }}
    >
      {children}
    </FileDetailViewerContext.Provider>
  );
}

function useFileDetailViewerContext() {
  return useContext(FileDetailViewerContext);
}

export { FileDetailViewerContext, FileDetailViewerContextProvider, useFileDetailViewerContext };
